/* Empowerment Fund training deck — "Would You Spend?"
 *
 * Swipeable card interface. On each card:
 *   1) User TAPS SPEND / DON'T / CALL (guess-first, no reveal until tap)
 *   2) Correct  → green flash + short coaching line + auto-advance
 *   3) Wrong    → red flash + longer coaching line, must tap Next to advance
 * At the end, POST /api/fund/quiz/complete → 80%+ activates the wallet.
 *
 * Trilingual — copy pulled from user.language_preference.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, ArrowRight, CheckCircle2, XCircle, PartyPopper, Phone, Wallet as WalletIcon } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ConfettiBurst from "../components/ConfettiBurst";

const T = {
    title:      { en: "Would You Spend?",             hi: "क्या आप खर्च करेंगे?",           bn: "আপনি খরচ করবেন?" },
    intro:      { en: "30 scenarios · complete once to activate your wallet.", hi: "30 परिदृश्य · वॉलेट सक्रिय करने के लिए एक बार पूरा करें।", bn: "৩০টি সিনারিও · ওয়ালেট চালু করতে একবার শেষ করুন।" },
    spend:      { en: "SPEND 🎉",                     hi: "खर्च 🎉",                       bn: "খরচ 🎉" },
    dont:       { en: "DON'T ✕",                      hi: "मत करो ✕",                       bn: "না ✕" },
    call:       { en: "CALL FH ☎",                    hi: "FH को कॉल ☎",                   bn: "FH-কে কল ☎" },
    correct:    { en: "Correct!",                      hi: "सही!",                          bn: "সঠিক!" },
    wrong:      { en: "Not quite —",                   hi: "बिल्कुल नहीं —",                bn: "ঠিক নয় —" },
    next:       { en: "Next",                          hi: "अगला",                          bn: "পরবর্তী" },
    of:         { en: "of",                            hi: "में से",                          bn: "মধ্যে" },
    finish:     { en: "Finish",                        hi: "समाप्त",                        bn: "শেষ" },
    congrats:   { en: "You passed! Your wallet is now ACTIVE.", hi: "आपने पास कर लिया! आपका वॉलेट अब सक्रिय है।", bn: "আপনি পাশ করেছেন! আপনার ওয়ালেট এখন সক্রিয়।" },
    almost:     { en: "You'll get it next time — need 24/30 to activate.", hi: "अगली बार पास हो जाएंगे — सक्रिय करने के लिए 24/30 चाहिए।", bn: "পরের বার হবে — অ্যাক্টিভ করতে দরকার ২৪/৩০।" },
    go_wallet:  { en: "Open my wallet",                hi: "मेरा वॉलेट खोलो",                bn: "আমার ওয়ালেট খুলুন" },
    retry:      { en: "Retry the deck",                hi: "फिर से कोशिश करें",              bn: "আবার চেষ্টা করুন" },
    coach_r:    { en: "Right answer — here's why:",    hi: "सही उत्तर — क्यों:",             bn: "সঠিক উত্তর — কেন:" },
};

const ANSWER_META = {
    SPEND: { key: "spend", icon: PartyPopper,     cls: "bg-emerald-500 hover:bg-emerald-600 text-white",   ring: "ring-emerald-400" },
    DONT:  { key: "dont",  icon: XCircle,         cls: "bg-red-500 hover:bg-red-600 text-white",           ring: "ring-red-400" },
    CALL:  { key: "call",  icon: Phone,           cls: "bg-amber-500 hover:bg-amber-600 text-white",       ring: "ring-amber-400" },
};

function langOf(u) { return u?.language_preference || u?.language || "en"; }
function tr(u, k) { return T[k]?.[langOf(u)] || T[k]?.en || k; }

export default function FundQuizPage() {
    const { user } = useAuth();
    const nav = useNavigate();
    const lang = langOf(user);
    const [deck, setDeck] = useState([]);
    const [idx, setIdx] = useState(0);
    const [pick, setPick] = useState(null);          // "SPEND"|"DONT"|"CALL"|null
    const [answers, setAnswers] = useState([]);       // {id, correct}
    const [done, setDone] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/fund/quiz/scenarios");
                setDeck(data?.deck || []);
            } catch (e) { toast.error(apiError(e)); }
        })();
    }, []);

    const total = deck.length;
    const card = deck[idx];
    const score = useMemo(() => answers.filter((a) => a.correct).length, [answers]);
    const progress = total ? Math.round(((idx + (pick ? 1 : 0)) / total) * 100) : 0;

    function pickAnswer(ans) {
        if (pick) return;
        const correct = ans === card.answer;
        setPick(ans);
        setAnswers((a) => [...a, { id: card.id, correct }]);
        if (correct) {
            setTimeout(() => advance(), 900);
        }
    }
    function advance() {
        setPick(null);
        if (idx + 1 >= total) {
            finishDeck();
        } else {
            setIdx(idx + 1);
        }
    }
    async function finishDeck() {
        setDone(true);
        try {
            const { data } = await api.post("/fund/quiz/complete", { score, total });
            setResult(data);
            if (data.passed) toast.success(tr(user, "congrats"));
        } catch (e) { toast.error(apiError(e)); }
    }

    if (!card && !done) {
        return <div className="mx-auto max-w-2xl p-6"><div className="h-64 rounded-2xl bg-white animate-pulse" /></div>;
    }

    if (done) {
        const passed = result?.passed;
        return (
            <div className="mx-auto max-w-2xl px-4 py-10" data-testid="fund-quiz-done">
                {passed && <ConfettiBurst count={60} duration={2000} />}
                <div className={`rounded-3xl p-8 text-center shadow-xl ${passed ? "bg-gradient-to-br from-emerald-100 via-white to-amber-50 border border-emerald-200" : "bg-gradient-to-br from-amber-50 via-white to-red-50 border border-amber-200"}`}>
                    <GraduationCap className="mx-auto h-16 w-16 text-[#1E3355] mb-3" />
                    <div className="text-4xl font-bold text-[#1E3355]" data-testid="fund-quiz-score">{score} / {total}</div>
                    <div className="mt-2 text-gray-700">{passed ? tr(user, "congrats") : tr(user, "almost")}</div>
                    <div className="mt-6 flex gap-2 justify-center">
                        {passed ? (
                            <button onClick={() => nav("/fund/wallet")} data-testid="fund-quiz-go-wallet" className="inline-flex items-center gap-2 rounded-full bg-[#1E3355] hover:bg-[#152541] text-white font-semibold px-5 py-2.5"><WalletIcon className="w-4 h-4" /> {tr(user, "go_wallet")}</button>
                        ) : (
                            <button onClick={() => { setDeck(deck); setIdx(0); setAnswers([]); setDone(false); setResult(null); }} data-testid="fund-quiz-retry" className="inline-flex items-center gap-2 rounded-full bg-[#1E3355] hover:bg-[#152541] text-white font-semibold px-5 py-2.5">{tr(user, "retry")}</button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const cardText = card[lang] || card.en;
    const showCoach = !!pick;
    const wasCorrect = pick === card.answer;

    return (
        <div className="mx-auto max-w-2xl px-4 py-6" data-testid="fund-quiz-page">
            <div className="flex items-baseline justify-between">
                <h1 className="text-2xl font-bold text-[#1E3355] inline-flex items-center gap-2"><GraduationCap className="w-6 h-6 text-violet-600" /> {tr(user, "title")}</h1>
                <div className="text-sm text-gray-500">{idx + 1} {tr(user, "of")} {total}</div>
            </div>
            <div className="text-xs text-gray-500">{tr(user, "intro")}</div>

            {/* Progress */}
            <div className="mt-3 h-1.5 rounded-full bg-gray-200 overflow-hidden" aria-hidden="true">
                <div className="h-full bg-violet-500 transition-[width] duration-500" style={{ width: `${progress}%` }} />
            </div>

            {/* Card */}
            <div className="mt-5 rounded-3xl bg-gradient-to-br from-white via-white to-violet-50 border-2 border-violet-200 shadow-xl p-6 min-h-[280px]" data-testid={`fund-quiz-card-${card.id}`}>
                <div className="text-[11px] uppercase tracking-widest text-violet-700 font-semibold mb-2">Scenario #{card.id}</div>
                <div className="text-xl font-medium text-[#1E3355] leading-relaxed">{cardText}</div>

                {showCoach && (
                    <div className={`mt-4 rounded-2xl p-3 ${wasCorrect ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`} data-testid={`fund-quiz-coach-${card.id}`}>
                        <div className={`text-xs font-bold uppercase tracking-widest ${wasCorrect ? "text-emerald-700" : "text-red-700"} inline-flex items-center gap-1`}>
                            {wasCorrect ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                            {wasCorrect ? tr(user, "correct") : tr(user, "wrong")}
                        </div>
                        <div className="text-sm text-gray-800 mt-1">{card.coach}</div>
                        {!wasCorrect && (
                            <button onClick={advance} data-testid={`fund-quiz-next-${card.id}`} className="mt-3 inline-flex items-center gap-1 rounded-full bg-[#1E3355] hover:bg-[#152541] text-white text-sm font-semibold px-4 py-1.5">
                                {tr(user, idx + 1 >= total ? "finish" : "next")} <ArrowRight className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                )}

                {/* Three big answer buttons */}
                <div className="mt-5 grid grid-cols-3 gap-2">
                    {["SPEND", "DONT", "CALL"].map((k) => {
                        const meta = ANSWER_META[k];
                        const Icon = meta.icon;
                        const selected = pick === k;
                        const isRight = pick && k === card.answer;
                        const isWrong = pick && k === pick && k !== card.answer;
                        return (
                            <button
                                key={k}
                                type="button"
                                data-testid={`fund-quiz-btn-${k.toLowerCase()}-${card.id}`}
                                disabled={!!pick}
                                onClick={() => pickAnswer(k)}
                                className={`rounded-2xl px-3 py-3 text-sm font-bold shadow-sm active:scale-95 transition disabled:cursor-default ${meta.cls} ${
                                    isRight ? "ring-4 " + meta.ring : ""
                                } ${isWrong ? "opacity-70" : ""}`}
                            >
                                <Icon className="w-5 h-5 mx-auto mb-1" />
                                {tr(user, meta.key)}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Running score */}
            <div className="mt-4 text-xs text-gray-500 text-center" data-testid="fund-quiz-running-score">Score so far: <span className="font-bold text-[#1E3355]">{score}</span> / {answers.length} · Need 24 / 30 to activate wallet</div>
        </div>
    );
}
